/**
 * CLIENT DASHBOARD - DSF Portal
 *
 * The main dashboard interface for client users in the Digital Safety File (DSF) Portal.
 * Provides an overview of key safety metrics and quick access to common workflows.
 *
 * FEATURES:
 * - Real-time dashboard metrics display (safety files, personnel, incidents, etc.)
 * - Interactive metric cards with navigation to detailed views
 * - Quick action cards for streamlined workflow automation
 * - Automatic button triggering for common tasks (add, import, create)
 * - Production-ready error handling and user feedback
 *
 * ARCHITECTURE:
 * - Uses custom useApiService hook for data fetching
 * - Implements proper TypeScript interfaces for type safety
 * - Responsive grid layout with MUI Joy UI Panels
 * - Toast notifications for user feedback
 * - Route-based navigation with React Router
 *
 * NAVIGATION AUTOMATION:
 * The dashboard includes intelligent navigation automation that not only routes users
 * to the correct pages but also attempts to trigger relevant actions (like opening
 * add/import dialogs) to streamline common workflows.
 */

// React core imports for component functionality
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

// MUI Joy UI Panels for modern, accessible interface design
import { Box, Button, Card, Chip, Grid, Typography } from "@mui/joy";

// PrimeReact Panels for enhanced UI functionality
import { Toast } from "primereact/toast";
import { ConfirmDialog } from "primereact/confirmdialog";

// Internal application imports
import useApiService from "../../hooks/useApiService";
import { RouteComponentProps } from "../../models/RouteComponent";
// import { getSiteCount } from "../../service/siteService";

// Material Design Icons for visual consistency across the dashboard
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PersonIcon from "@mui/icons-material/Person";
import HandymanIcon from "@mui/icons-material/Handyman";
import WarningIcon from "@mui/icons-material/Warning";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import DriveEtaIcon from "@mui/icons-material/DriveEta";
import ScienceIcon from "@mui/icons-material/Science";
import SecurityIcon from "@mui/icons-material/Security";
import ArticleIcon from "@mui/icons-material/Article";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import BadgeIcon from "@mui/icons-material/Badge";

/**
 * TypeScript interface for dashboard metrics response from the API
 *
 * Defines the structure of data returned by the dashboard API endpoint.
 * All counts represent current totals for the client's organization.
 */
interface DashboardMetrics {
  safety_file_count: number; // Number of active digital safety files
  employee_count: number; // Total registered employees in the system
  incident_count: number; // Total recorded safety incidents and near-misses
  task_count: number; // Number of compliance and safety tasks
  equipment_count: number; // Total registered tools and equipment items
  vehicle_count: number; // Number of vehicles and mobile plant registered
  chemical_count: number; // Count of hazardous chemicals in inventory
  ppe_count: number; // Personal protective equipment items registered
}

/**
 * The API returns the metrics directly, not wrapped in a content object
 * This interface extends DashboardMetrics without additional properties
 */
interface DashboardResponse extends DashboardMetrics {}

/**
 * Props interface for the StatCard component
 * Defines the structure for metric display cards with optional interactivity
 */
interface StatCardProps {
  title: string; // Display name for the metric (e.g., "Active Safety Files")
  value: string | number; // The numeric value or formatted string to display
  icon: React.ReactNode; // Material Design icon component
  trend?: string; // Optional trend indicator: 'up', 'down', or neutral
  trendValue?: string; // Optional trend percentage or value (e.g., "+5%")
  color: "primary" | "success" | "warning" | "danger" | "neutral"; // Card color theme
  onClick?: () => void; // Optional click handler for navigation
}

/**
 * Props interface for the ActionCard component
 * Defines the structure for quick action cards that trigger workflows
 */
interface ActionCardProps {
  title: string; // Action title (e.g., "Add Employee")
  description: string; // Brief description of what the action does
  icon: React.ReactNode; // Material Design icon representing the action
  link: string; // Fallback navigation route if onClick is not provided
  color: string; // Theme color for the card styling
  buttonText?: string; // Optional custom button text (defaults to "View")
  onClick?: () => void; // Optional custom click handler for advanced workflows
}

/**
 * StatCard Component - Displays a metric card with optional click functionality
 *
 * This component renders individual metric cards that show key safety statistics.
 * Cards can be interactive (clickable) or display-only depending on the onClick prop.
 *
 * Features:
 * - Responsive design with hover effects for interactive cards
 * - Color-themed icons with consistent visual hierarchy
 * - Optional trend indicators with directional arrows
 * - Smooth animations and transitions
 *
 * @param title - The title of the metric (displayed as secondary text)
 * @param value - The numeric value to display prominently
 * @param icon - The icon to display (Material Design icon component)
 * @param trend - Optional trend direction ('up', 'down', or neutral)
 * @param trendValue - Optional trend value to display (e.g., "+5%", "-2%")
 * @param color - Color theme for the card (affects icon background and hover states)
 * @param onClick - Optional click handler for navigation (makes card interactive)
 */
const StatCard = ({ title, value, icon, trend, trendValue, color, onClick }: StatCardProps) => {
  return (
    <Card
      variant="outlined"
      onClick={onClick}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        // Smooth transition for hover effects
        transition: "all 0.2s",
        // Dynamic cursor based on whether card is clickable
        cursor: onClick ? "pointer" : "default",
        // Enhanced hover effects for interactive cards
        "&:hover": {
          boxShadow: onClick ? "lg" : "md",
          borderColor: onClick ? "primary.outlinedHoverBorder" : "neutral.outlinedHoverBorder",
          // Subtle lift effect for clickable cards
          transform: onClick ? "translateY(-2px)" : "none",
        },
      }}
    >
      {/* Card header with title and icon */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography level="body-sm" textColor="text.secondary">
          {title}
        </Typography>
        {/* Circular icon container with theme-aware coloring */}
        <Box
          sx={{
            backgroundColor: `${color}.softBg`,
            color: `${color}.solidColor`,
            borderRadius: "50%",
            width: 35,
            height: 35,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
      </Box>
      {/* Main metric value display */}
      <Typography level="h3">{value}</Typography>
      {/* Optional trend indicator with color-coded direction */}
      {trend && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
          <Chip
            size="sm"
            variant="soft"
            // Dynamic color based on trend direction
            color={trend === "up" ? "success" : trend === "down" ? "danger" : "neutral"}
            startDecorator={
              // Arrow indicators for trend direction
              trend === "up" ? "↑" : trend === "down" ? "↓" : "="
            }
          >
            {trendValue}
          </Chip>
          <Typography level="body-sm">vs last month</Typography>
        </Box>
      )}
    </Card>
  );
};

/**
 * ActionCard Component - Displays a quick action card with navigation
 *
 * This component renders action cards that provide shortcuts to common workflows.
 * Each card includes an icon, title, description, and an action button that can
 * either navigate to a page directly or trigger custom onClick behavior.
 *
 * Features:
 * - Consistent visual design with themed icons
 * - Hover effects for enhanced user experience
 * - Flexible click handling (custom onClick or fallback navigation)
 * - Customizable button text
 *
 * @param title - The title of the action (e.g., "Add Employee")
 * @param description - Description of what the action does
 * @param icon - The icon to display (Material Design icon component)
 * @param link - The navigation link (used as fallback if onClick not provided)
 * @param color - Color theme for the card styling
 * @param buttonText - Optional custom button text (defaults to "View")
 * @param onClick - Optional click handler for custom navigation workflows
 */
const ActionCard = ({
  title,
  description,
  icon,
  link,
  color,
  buttonText = "View",
  onClick,
}: ActionCardProps) => {
  const navigate = useNavigate();

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        cursor: "pointer",
        // Smooth hover transitions
        transition: "all 0.2s",
        "&:hover": {
          boxShadow: "md",
          borderColor: "neutral.outlinedHoverBorder",
        },
      }}
      // Use custom onClick if provided, otherwise navigate to fallback link
      onClick={() => (onClick ? onClick() : navigate(link))}
    >
      {/* Card content with icon and text */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
        {/* Rounded icon container with theme coloring */}
        <Box
          sx={{
            backgroundColor: `${color}.softBg`,
            color: `${color}.solidColor`,
            borderRadius: "12px",
            p: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
        {/* Action title and description */}
        <Box>
          <Typography level="title-md">{title}</Typography>
          <Typography level="body-sm" sx={{ mt: 0.5 }} textColor="text.secondary">
            {description}
          </Typography>
        </Box>
      </Box>
      {/* Action button with arrow indicator */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", mt: 2 }}>
        <Button variant="plain" color="primary" endDecorator={<ArrowForwardIcon />} size="sm">
          {buttonText}
        </Button>
      </Box>
    </Card>
  );
};

/**
 * ClientDashboard Component - Main dashboard for client users
 *
 * This is the primary dashboard interface that client users see when they log into
 * the DSF Portal. It provides a comprehensive overview of safety metrics and quick
 * access to common workflows through interactive cards.
 *
 * Key Features:
 * - Real-time dashboard metrics fetched from the API
 * - Interactive metric cards that navigate to detailed management pages
 * - Quick action cards with intelligent workflow automation
 * - Automatic button triggering to streamline user tasks
 * - Production-ready error handling with user-friendly notifications
 * - Responsive grid layout that adapts to different screen sizes
 *
 * Architecture:
 * - Uses the useApiService hook for data fetching with built-in error handling
 * - Implements the RouteComponentProps interface for consistent navigation
 * - Leverages MUI Joy UI Panels for modern, accessible design
 * - Integrates PrimeReact Panels for enhanced functionality
 *
 * @param props - Route component props including session and global loader state
 */
export default function ClientDashboard(props: RouteComponentProps) {
  // Toast notification reference for user feedback
  const toast = useRef(null as any);
  // Navigation hook for programmatic routing
  const navigate = useNavigate();
  // Ref to track last toast to prevent duplicates in React StrictMode
  const lastToastRef = useRef<{ message: string; timestamp: number } | null>(null);

  // State for dashboard metrics - initialized with zero values
  // This ensures the UI displays properly while data is loading
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    safety_file_count: 0,
    employee_count: 0,
    incident_count: 0,
    task_count: 0,
    equipment_count: 0,
    vehicle_count: 0,
    chemical_count: 0,
    ppe_count: 0,
  });

  // State for site count from the site-list API
  const [siteCount, setSiteCount] = useState<number>(0);

  // API hook for dashboard data with automatic fetching on component mount
  const { loading: dashboardLoading } = useApiService<DashboardResponse>({
    url: `dashboard`,
    method: "GET",
    apiName: "core",
    fetchOnMount: true,
    cache: {
      enabled: true,
      strategy: "cache-first",
      key: `client-dashboard:${props.session.client_id}`,
    },
    // Success handler: Updates local state and shows success notification
    onSuccess: (response: DashboardResponse) => {
      console.info("Dashboard metrics loaded", {
        timestamp: new Date().toISOString(),
        recordCount: Object.values(response).reduce((sum, count) => sum + count, 0),
      });
      // Update the metrics state with fresh data from API
      setMetrics(response);

      // Prevent duplicate toasts in React StrictMode
      const currentTime = Date.now();
      const message = "Dashboard Updated";
      if (
        !lastToastRef.current ||
        lastToastRef.current.message !== message ||
        currentTime - lastToastRef.current.timestamp > 2000
      ) {
        // Notify user of successful data refresh
        toast.current?.show({
          severity: "success",
          summary: "Dashboard Updated",
          detail: "Latest metrics have been loaded successfully.",
        });

        // Update the ref to track this toast
        lastToastRef.current = { message, timestamp: currentTime };
      }
    },
    // Error handler: Logs error and notifies user, but allows fallback to default values
    onError: (error: any) => {
      console.warn("Dashboard metrics load failed", {
        timestamp: new Date().toISOString(),
        error: error?.message || "Unknown error",
      });
      // Inform user that default values are being used
      toast.current?.show({
        severity: "warn",
        summary: "Dashboard Data",
        detail: "Using default values. Could not fetch latest metrics.",
      });
    },
  });

  // Synchronize global loader state with dashboard loading state
  // This shows/hides the global loading indicator based on API call status
  useEffect(() => {
    props.setGlobalLoader(dashboardLoading);
  }, [dashboardLoading, props]);

  // Fetch site count from the site-list API endpoint
  useEffect(() => {
    const fetchSiteCount = async () => {
      try {
        // const count = await getSiteCount();
        // setSiteCount(count);
        // console.info("Site count loaded", {
        //   timestamp: new Date().toISOString(),
        //   count: count,
        // });
      } catch (error) {
        console.warn("Site count load failed", {
          timestamp: new Date().toISOString(),
          error: error instanceof Error ? error.message : "Unknown error",
        });
        // Keep the default value of 0
      }
    };

    fetchSiteCount();
  }, []);

  /**
   * Dashboard statistics configuration
   * Creates clickable metric cards that navigate to specific pages when clicked.
   * The cards are organized in rows and display real-time data from the API.
   *
   * Each stat card includes:
   * - title: Display name for the metric
   * - value: Numeric value from the API response
   * - icon: Visual icon representing the metric type
   * - color: Theme color for the card styling
   * - onClick: Navigation function to the relevant management page
   */
  const statsData: StatCardProps[] = [
    // First Row: Core Safety Metrics
    // These represent the fundamental safety data points that clients monitor most frequently
    {
      title: "Active Safety Files",
      value: metrics.safety_file_count,
      icon: <ArticleIcon />,
      color: "primary",
      // Navigate to site management when clicked
      onClick: () => navigate("/site-table"),
    },
    {
      title: "Personnel",
      value: metrics.employee_count,
      icon: <PersonIcon />,
      color: "success",
      // Navigate to employee management when clicked
      onClick: () => navigate("/employee-table"),
    },
    {
      title: "Incidents",
      value: metrics.incident_count,
      icon: <WarningIcon />,
      // Dynamic color: red if incidents exist, green if none
      color: metrics.incident_count > 0 ? "danger" : "success",
      // Navigate to incident reporting/viewing when clicked
      onClick: () => navigate("/incident-view"),
    },
    {
      title: "Compliance Actions",
      value: metrics.task_count,
      icon: <TaskAltIcon />,
      color: "success",
      // Navigate to task management when clicked
      onClick: () => navigate("/task-view"),
    },
    // Second Row: Equipment and Resources
    // These metrics track physical assets and safety equipment
    {
      title: "Tools & Equipment",
      value: metrics.equipment_count,
      icon: <HandymanIcon />,
      color: "primary",
      // Navigate to equipment management when clicked
      onClick: () => navigate("/equipment-table"),
    },
    {
      title: "Vehicles & Mobile Plant",
      value: metrics.vehicle_count,
      icon: <DriveEtaIcon />,
      color: "warning",
      // Navigate to vehicle management when clicked
      onClick: () => navigate("/vehicle-table"),
    },
    {
      title: "Hazardous Chemicals",
      value: metrics.chemical_count,
      icon: <ScienceIcon />,
      color: "danger",
      // Navigate to chemical management when clicked
      onClick: () => navigate("/chemical-table"),
    },
    {
      title: "PPE Issue Register",
      value: metrics.ppe_count,
      icon: <SecurityIcon />,
      color: "neutral",
      // Navigate to PPE management when clicked
      onClick: () => navigate("/ppe-table"),
    },
  ];

  /**
   * Navigate to a route and attempt to trigger an action (like clicking Add or Import button)
   *
   * This utility function streamlines user workflows by:
   * 1. Navigating to the target page
   * 2. Waiting for page Panels to load (1 second delay)
   * 3. Automatically finding and clicking relevant action buttons
   *
   * The function uses a priority-based approach:
   * - For 'import' actions: Prioritizes import/upload buttons over general add buttons
   * - For other actions: Looks for add/create/new buttons with fallback to common patterns
   *
   * @param route - The destination route path (e.g., '/employee-table')
   * @param actionToTrigger - Action type to trigger ('import', 'add', 'create', etc.)
   */
  const navigateAndTriggerAction = (route: string, actionToTrigger: string) => {
    // Log the navigation attempt for debugging and monitoring
    console.info("Navigation with auto-action", {
      timestamp: new Date().toISOString(),
      route,
      action: actionToTrigger,
    });
    // Perform the navigation first
    navigate(route);

    // Allow sufficient time for page Panels to render and initialize
    // This delay is crucial for the DOM manipulation to work reliably
    setTimeout(() => {
      // For import actions, prioritize import-specific buttons
      if (actionToTrigger.toLowerCase() === "import") {
        // Strategy 1: Look specifically for import buttons by text content
        const allButtons = document.querySelectorAll("button");
        const importButton = Array.from(allButtons).find(button => {
          const text = button.textContent?.toLowerCase().trim();
          return text?.includes("import") || text?.includes("upload");
        });

        if (importButton) {
          console.info("Auto-triggered import button", {
            timestamp: new Date().toISOString(),
            action: importButton.textContent?.trim(),
          });
          importButton.click();
          return;
        }

        // Strategy 2: Fallback to looking for import-specific icons and attributes
        const importSelectors = [
          "button .pi-upload", // PrimeReact upload/import icon
          "button .pi-file-import", // PrimeReact file import icon
          'button[aria-label*="Import"]', // Import accessibility labels
          'button[title*="Import"]', // Import title attributes
          'button[aria-label*="Upload"]', // Upload accessibility labels
          'button[title*="Upload"]', // Upload title attributes
        ];

        // Try each selector until we find a matching button
        for (const selector of importSelectors) {
          const button = document.querySelector(selector);
          if (button) {
            console.info("Auto-triggered import via selector", {
              timestamp: new Date().toISOString(),
              selector,
            });
            (button as HTMLElement).click();
            return;
          }
        }
      } else {
        // For other actions (add, create, etc.), look for general action buttons
        // Strategy 1: Search by button text content
        const allButtons = document.querySelectorAll("button");
        const targetButton = Array.from(allButtons).find(button => {
          const text = button.textContent?.toLowerCase().trim();
          return (
            text?.includes(actionToTrigger.toLowerCase()) ||
            text?.includes("add") ||
            text?.includes("create") ||
            text?.includes("new")
          );
        });

        if (targetButton) {
          console.info(`Auto-triggered ${actionToTrigger} button`, {
            timestamp: new Date().toISOString(),
            action: actionToTrigger,
            element: targetButton.textContent?.trim(),
          });
          targetButton.click();
          return;
        }

        // Strategy 2: Fallback to looking for buttons with common class patterns
        const buttonSelectors = [
          "button .pi-plus", // PrimeReact plus icon
          ".p-button-outlined", // PrimeReact outlined button
          'button[aria-label*="Add"]', // Accessibility labels
          'button[title*="Add"]', // Title attributes
        ];

        // Try each selector until we find a matching button
        for (const selector of buttonSelectors) {
          const button = document.querySelector(selector);
          if (button) {
            console.info(`Auto-triggered ${actionToTrigger} via selector`, {
              timestamp: new Date().toISOString(),
              action: actionToTrigger,
              selector,
            });
            (button as HTMLElement).click();
            break;
          }
        }
      }
    }, 1000); // 1-second delay to allow page Panels to initialize
  };

  /**
   * Navigate to client view with Documents tab pre-selected
   *
   * This function uses URL parameters to ensure the Documents tab is automatically
   * selected when the client-view page loads. This provides a seamless user experience
   * for the Company Compliance quick action card.
   *
   * The URL parameter approach is reliable across different browsers and ensures
   * consistent tab selection behavior.
   */
  const navigateToClientViewDocuments = () => {
    // Log the navigation for monitoring purposes
    console.info("Navigation to client documents", {
      timestamp: new Date().toISOString(),
      route: "/documents-table",
    });
    // Navigate with tab parameter to auto-select documents tab
    navigate("/documents-table");
  };

  /**
   * Quick actions configuration for the dashboard
   * Provides shortcuts to common tasks with automatic navigation and action triggering.
   *
   * These action cards streamline common workflows by:
   * 1. Navigating to the appropriate page
   * 2. Automatically triggering relevant actions (like opening add/import dialogs)
   * 3. Reducing clicks needed for frequently used features
   *
   * The actions are organized by functional area:
   * - Core Safety Management (safety files, personnel, incidents)
   * - Equipment and Resources (tools, vehicles, chemicals, PPE)
   * - Compliance and Documentation (admin users, emergency contacts, attendance)
   */
  const quickActions: ActionCardProps[] = [
    // Row 1: Core Safety Management
    {
      title: "Create Safety File",
      description: "Set up a new digital safety file",
      icon: <ArticleIcon />,
      link: "/site-table",
      color: "primary",
      buttonText: "Get started",
      onClick: () => navigateAndTriggerAction("/site-table", "add"),
    },
    {
      title: "Add Employee",
      description: "Add new employees to your database",
      icon: <PersonIcon />,
      link: "/employee-table",
      color: "success",
      buttonText: "Add now",
      onClick: () => navigateAndTriggerAction("/employee-table", "add"),
    },
    {
      title: "Add Sub-contractor",
      description: "Register subcontractors for your projects",
      icon: <BadgeIcon />,
      link: "/subcontractor-table",
      color: "warning",
      buttonText: "Register",
      onClick: () => navigateAndTriggerAction("/subcontractor-table", "add"),
    },
    {
      title: "Add Tools & Equipment",
      description: "Register new tools and equipment",
      icon: <HandymanIcon />,
      link: "/equipment-table",
      color: "neutral",
      buttonText: "Add items",
      onClick: () => navigateAndTriggerAction("/equipment-table", "import"),
    },
    // Row 2: Equipment and Safety Resources
    {
      title: "Add Vehicles",
      description: "Register vehicles and mobile plant",
      icon: <DriveEtaIcon />,
      link: "/vehicle-table",
      color: "primary",
      buttonText: "Register",
      onClick: () => navigateAndTriggerAction("/vehicle-table", "add"),
    },
    {
      title: "Add Chemicals",
      description: "Register hazardous chemicals",
      icon: <ScienceIcon />,
      link: "/chemical-table",
      color: "danger",
      buttonText: "Add chemicals",
      onClick: () => navigateAndTriggerAction("/chemical-table", "add"),
    },
    {
      title: "Log Incident",
      description: "Report a safety incident or near-miss",
      icon: <WarningIcon />,
      link: "/incident-view",
      color: "danger",
      buttonText: "Report now",
      onClick: () => navigateAndTriggerAction("/incident-view", "add"),
    },
    {
      title: "Add Admin User",
      description: "Add users with administrative access",
      icon: <SecurityIcon />,
      link: "/client-admin",
      color: "warning",
      buttonText: "Add admin",
      onClick: () => navigateAndTriggerAction("/client-admin", "add"),
    },
    // Row 3: Compliance and Documentation
    {
      title: "Add PPE",
      description: "Register personal protective equipment",
      icon: <SecurityIcon />,
      link: "/ppe-table",
      color: "success",
      buttonText: "Add PPE",
      onClick: () => navigateAndTriggerAction("/ppe-table", "import"),
    },
    {
      title: "Document management setup",
      description: "Manage OSH Act compliance requirements",
      icon: <TaskAltIcon />,
      link: "/client-view",
      color: "primary",
      buttonText: "Setup Documents",
      onClick: () => navigateToClientViewDocuments(),
    },
    {
      title: "Emergency Contacts",
      description: "Update emergency contact information",
      icon: <ReceiptLongIcon />,
      link: "/emergency-contact",
      color: "danger",
      buttonText: "Add contacts",
    },
    {
      title: "Attendance Register",
      description: "Record and track site attendance",
      icon: <EventAvailableIcon />,
      link: "/attendance-table",
      color: "neutral",
      buttonText: "Manager register",
    },
  ];

  return (
    <React.Fragment>
      <Box
        sx={{
          display: "flex",
          mb: 2,
          gap: 1,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "start", sm: "center" },
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Typography level="h2" component="h1">
          Welcome to the Client Portal
        </Typography>
      </Box>

      {/* Statistics Cards - First Row */}
      <Typography level="title-md" sx={{ mb: 1 }}>
        Key Metrics
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statsData.slice(0, 4).map((stat, index) => (
          <Grid xs={12} sm={6} md={3} key={index}>
            <StatCard {...stat} />
          </Grid>
        ))}
      </Grid>

      {/* Statistics Cards - Second Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statsData.slice(4, 8).map((stat, index) => (
          <Grid xs={12} sm={6} md={3} key={index + 4}>
            <StatCard {...stat} />
          </Grid>
        ))}
      </Grid>

      {/* Statistics Cards - Third Row */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {statsData.slice(8, 12).map((stat, index) => (
          <Grid xs={12} sm={6} md={3} key={index + 8}>
            <StatCard {...stat} />
          </Grid>
        ))}
      </Grid>

      {/* Quick Actions Grid */}
      <Typography level="title-md" sx={{ mb: 1 }}>
        Quick Actions
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {quickActions.slice(0, 4).map((action, index) => (
          <Grid xs={12} sm={6} md={3} key={index}>
            <ActionCard {...action} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        {quickActions.slice(4, 8).map((action, index) => (
          <Grid xs={12} sm={6} md={3} key={index + 4}>
            <ActionCard {...action} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        {quickActions.slice(8, 12).map((action, index) => (
          <Grid xs={12} sm={6} md={3} key={index + 8}>
            <ActionCard {...action} />
          </Grid>
        ))}
      </Grid>

      <ConfirmDialog />
      <Toast ref={toast} />
    </React.Fragment>
  );
}
